import type { VariantStock } from "@cleanbudget/contract/stock";
import { Input } from "@cleanbudget/ui/components/input";
import { IconChevronRight, IconScan, IconX } from "@tabler/icons-react";
import { keepPreviousData, useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { match } from "ts-pattern";
import { BackLink } from "@/components/back-link";
import { PageHeader } from "@/components/page-header";
import { orpc } from "@/lib/orpc";
import { ArchivedProductNotice } from "./-components/archived-product-notice";
import { ComposedBundleNotice } from "./-components/composed-bundle-notice";
import { QuickCorrection } from "./-components/quick-correction";
import { QuickSearch } from "./-components/search";

const RESULTS = 10;

const variantQuery = (variantId: string) => orpc.stock.getVariant.queryOptions({ input: { variantId } });
const resultsQuery = (search: string) =>
	orpc.stock.list.queryOptions({ input: { search, pageSize: RESULTS, page: 1 } });

/** A barcode or SKU typed in full: that variant, without a tap. */
const exactly = (stock: VariantStock, search: string) =>
	stock.ean === search || stock.sku.toLowerCase() === search.toLowerCase();

/**
 * Snelle correctie (#67), for a phone in the warehouse: type or scan a name, SKU or EAN, tap the
 * variant (an exact SKU or EAN opens it at once), tap − or +, pick a reason, save. What was typed
 * and the variant picked are the URL, so Back returns to the results.
 */
export const Route = createFileRoute("/_admin/stock/quick")({
	validateSearch: QuickSearch,
	loaderDeps: ({ search }) => ({ variantId: search.variantId }),
	loader: ({ context, deps }) =>
		deps.variantId ? context.queryClient.ensureQueryData(variantQuery(deps.variantId)) : undefined,
	component: QuickPage,
});

function QuickPage() {
	const { t } = useTranslation("stock");
	const { variantId, search } = Route.useSearch();

	return (
		<div className="mx-auto flex w-full max-w-md flex-col gap-4">
			<PageHeader
				back={<BackLink to="/stock">{t("title")}</BackLink>}
				title={t("quick.title")}
				description={t("quick.description")}
			/>
			<SearchBox value={search} />
			{variantId ? <Picked variantId={variantId} /> : <Results search={search ?? ""} />}
		</div>
	);
}

/** The search box: big, focused on arrival, the URL following the typing after a short pause. */
function SearchBox({ value }: { value: string | undefined }) {
	const { t } = useTranslation("stock");
	const navigate = useNavigate({ from: Route.fullPath });
	const id = useId();
	const [text, setText] = useState(value ?? "");

	useEffect(() => setText(value ?? ""), [value]);

	useEffect(() => {
		const wanted = text.trim();
		if (wanted === (value ?? "")) return;
		const timer = setTimeout(
			() => void navigate({ search: { search: wanted || undefined }, replace: true }),
			250,
		);
		return () => clearTimeout(timer);
	}, [text, value, navigate]);

	return (
		<form
			role="search"
			className="relative"
			onSubmit={(event) => {
				event.preventDefault();
				void navigate({ search: { search: text.trim() || undefined }, replace: true });
			}}
		>
			<label htmlFor={id} className="sr-only">
				{t("quick.searchLabel")}
			</label>
			<IconScan
				className="pointer-events-none absolute top-1/2 left-4 size-5 -translate-y-1/2 text-muted-foreground"
				aria-hidden
			/>
			<Input
				id={id}
				type="text"
				inputMode="search"
				value={text}
				onChange={(event) => setText(event.target.value)}
				placeholder={t("quick.searchPlaceholder")}
				enterKeyHint="search"
				autoComplete="off"
				autoCapitalize="off"
				spellCheck={false}
				autoFocus
				className="h-14 rounded-2xl pr-12 pl-12 text-lg"
			/>
			{text ? (
				<button
					type="button"
					className="absolute top-1/2 right-2 flex size-10 -translate-y-1/2 items-center justify-center rounded-full text-muted-foreground hover:text-foreground"
					aria-label={t("quick.back")}
					onClick={() => {
						setText("");
						void navigate({ search: {}, replace: true });
					}}
				>
					<IconX className="size-5" aria-hidden />
				</button>
			) : null}
		</form>
	);
}

/**
 * The correction for a variant, or why there is none: a composed bundle has no stock of its own, an
 * archived product's variant is read-only until it is restored.
 */
function QuickPanel({ stock }: { stock: VariantStock }) {
	const blocked = match(stock)
		.with({ archived: true }, () => <ArchivedProductNotice stock={stock} />)
		.with({ composedBundle: true }, () => <ComposedBundleNotice stock={stock} />)
		.otherwise(() => null);
	if (blocked === null) return <QuickCorrection stock={stock} />;
	return (
		<section className="flex flex-col gap-4 rounded-2xl bg-card p-4 ring-1 ring-foreground/10">
			<div className="flex flex-col gap-1">
				<h2 className="font-heading text-lg leading-tight font-semibold">{stock.productName}</h2>
				<p className="font-mono text-sm text-muted-foreground">{stock.sku}</p>
			</div>
			{blocked}
		</section>
	);
}

/** The variant picked from the results. */
function Picked({ variantId }: { variantId: string }) {
	const { data } = useSuspenseQuery(variantQuery(variantId));
	return <QuickPanel key={data.variantId} stock={data} />;
}

/** What matches the search, as big rows to tap; an exact SKU or EAN opens straight away. */
function Results({ search }: { search: string }) {
	const { t } = useTranslation("stock");
	const { data, isFetching, isPlaceholderData } = useQuery({
		...resultsQuery(search),
		enabled: search !== "",
		placeholderData: keepPreviousData,
	});

	if (search === "") return <p className="px-1 text-sm text-muted-foreground">{t("quick.hint")}</p>;
	if (!data) return null;

	const [first] = data.items;
	if (
		first &&
		exactly(first, search) &&
		(data.items.length === 1 || first.ean === search) &&
		!isPlaceholderData
	) {
		return <QuickPanel key={first.variantId} stock={first} />;
	}
	if (data.items.length === 0) {
		return isFetching ? null : (
			<p className="px-1 text-sm text-muted-foreground">{t("quick.none", { search })}</p>
		);
	}

	return (
		<div className="flex flex-col gap-2">
			<ul className="flex flex-col overflow-hidden rounded-2xl bg-card ring-1 ring-foreground/10">
				{data.items.map((stock) => (
					<li key={stock.variantId} className="border-b last:border-b-0">
						<Link
							to="."
							search={{ search, variantId: stock.variantId }}
							aria-label={t("quick.select", { name: `${stock.productName} ${stock.variantName}` })}
							className="flex min-h-16 items-center gap-3 px-4 py-3 active:bg-muted"
						>
							<span className="flex min-w-0 flex-1 flex-col">
								<span className="truncate font-medium">{stock.productName}</span>
								<span className="truncate text-sm text-muted-foreground">
									{stock.variantName === stock.productName ? null : `${stock.variantName} · `}
									<span className="font-mono">{stock.sku}</span>
								</span>
							</span>
							<span className="text-xl font-semibold tabular-nums">{stock.quantity}</span>
							<IconChevronRight className="size-5 shrink-0 text-muted-foreground" aria-hidden />
						</Link>
					</li>
				))}
			</ul>
			{data.total > data.items.length ? (
				<p className="px-1 text-sm text-muted-foreground">{t("quick.more")}</p>
			) : null}
		</div>
	);
}
